import { useState, useEffect } from 'react'
import {
  verifyEmail as verifyEmailService,
  VerificationError,
  ErrorReason,
} from '../../service/verification-service'

interface UseVerificationResult {
  isEmailVerified: boolean
  isLoading: boolean
  error: string | null
  errorReason: ErrorReason | string | null
  isTokenExpired: boolean
  isTokenInvalid: boolean
  isTooManyRequests: boolean
}

export const useVerification = (
  token: string | null,
  env: string | null = null
): UseVerificationResult => {
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorReason, setErrorReason] = useState<ErrorReason | string | null>(
    null
  )
  const [isTokenExpired, setIsTokenExpired] = useState(false)
  const [isTokenInvalid, setIsTokenInvalid] = useState(false)
  const [isTooManyRequests, setIsTooManyRequests] = useState(false)

  useEffect(() => {
    if (!token) {
      return
    }

    const verify = async () => {
      setIsLoading(true)
      setError(null)
      setErrorReason(null)
      setIsTokenExpired(false)
      setIsTokenInvalid(false)
      setIsTooManyRequests(false)

      try {
        await verifyEmailService(token, env)
        setIsEmailVerified(true)
      } catch (err) {
        if (err instanceof VerificationError) {
          setError(err.message)
          setErrorReason(err.reason || null)
          setIsTokenExpired(err.isTokenExpired())
          setIsTokenInvalid(err.isTokenInvalid())
          setIsTooManyRequests(err.isTooManyRequests())
        } else {
          setError(err instanceof Error ? err.message : 'Verification failed')
        }
        setIsEmailVerified(false)
      } finally {
        setIsLoading(false)
      }
    }

    verify()
  }, [token, env])

  return {
    isEmailVerified,
    isLoading,
    error,
    errorReason,
    isTokenExpired,
    isTokenInvalid,
    isTooManyRequests,
  }
}

export default useVerification
